import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { ConfirmDialog } from "../ConfirmDialog";
import { ConfirmDialogTitle } from "../ConfirmDialog/ConfirmDialogTitle";
import { ConfirmDialogBody } from "../ConfirmDialog/ConfirmDialogBody";
import { ConfirmDialogActions } from "../ConfirmDialog/ConfirmDialogActions";
import type { TagDeleteDialogProps } from "./types";

export function TagDeleteDialog({
  open,
  onClose,
  tagName,
  usageCount,
  onConfirm,
}: TagDeleteDialogProps) {
  const { t } = useTranslation(["common"]);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfirmDialog open={open} onClose={onClose} forced>
      <ConfirmDialogTitle>{t("tags.deleteTag")}</ConfirmDialogTitle>
      <ConfirmDialogBody>
        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm text-primary">
                {t("tags.deleteMessage", { tagName })}
              </p>
              {usageCount !== undefined && usageCount > 0 && (
                <p className="text-sm text-secondary">
                  {t("tags.usedByDocuments", { count: usageCount })}
                </p>
              )}
              {usageCount !== undefined && usageCount === 0 && (
                <p className="text-sm text-secondary">
                  {t("tags.notUsedByDocuments")}
                </p>
              )}
            </div>
          </div>
        </div>
      </ConfirmDialogBody>
      <ConfirmDialogActions
        onConfirm={handleConfirm}
        onCancel={onClose}
        confirmLabel={t("buttons.delete")}
        variant="danger"
        loading={loading}
      />
    </ConfirmDialog>
  );
}
